/**
 * teamRows — which Teams the Usage pivot shows, in what order, and what the
 * rest sum to, for workspaces with dozens of Teams (mostly personal ones).
 *
 * Pure: the section owns the search, kind, sort and Show all state and hands
 * it here. Every pivot Team lands in exactly one of `rows`, `noTeam` or
 * `hidden`, so the shown rows plus the rollup row plus No Team always add up
 * to the Agent total footer, which keeps covering the whole report.
 *
 * The whole pivot comes in one response and is shaped here: fine for low
 * hundreds of Teams. Past that, the server should page it.
 */
import type {
  YagniCodeIdleTeam,
  YagniCodeTeamAgentUsageCell,
  YagniCodeTeamAgentUsageReport,
  YagniCodeTeamOwner,
  YagniCodeTeamUsageTotal,
} from "@yagni/shared";

/** Named Teams shown before the rest fold into one rollup row. */
export const TEAM_ROW_LIMIT = 10;

export type TeamKind = "all" | "shared" | "personal";
export type TeamSort = "billed" | "saved" | "name";

export interface TeamRowsView {
  /** Named Teams shown, in sort order. */
  rows: YagniCodeTeamUsageTotal[];
  /** Shown pinned last when kind is "all" and the query is empty. */
  noTeam: YagniCodeTeamUsageTotal | null;
  /** Every pivot Team not shown: capped or filtered out, No Team included
   * when it is not shown. */
  hidden: YagniCodeTeamUsageTotal[];
  /** Named Teams in `hidden`: what the rollup row counts as Teams. No Team
   * rides along in the sum under a filter but is not a Team. */
  hiddenNamedCount: number;
  hiddenReason: "cap" | "filter" | null;
  /** The report's idle Teams after the query and kind filter. */
  idle: YagniCodeIdleTeam[];
  /** Named Teams in the pivot (No Team and idle Teams excluded). */
  namedCount: number;
  /** Personal Teams across the pivot and the idle list. */
  personalCount: number;
  /** Teams the query and kind match, pivot and idle together. */
  matchedCount: number;
}

export interface TeamRowsOptions {
  query: string;
  kind: TeamKind;
  sort: TeamSort;
  showAll: boolean;
  limit?: number;
}

/** Lowercased, trimmed and accent-free, so "alvarez" finds "Álvarez". */
function fold(text: string): string {
  return text.trim().normalize("NFD").replace(/\p{M}/gu, "").toLowerCase();
}

interface TeamIdentity {
  deskId: string | null;
  deskName: string | null;
  personalOwner?: YagniCodeTeamOwner | null;
}

function matchesKind(team: TeamIdentity, kind: TeamKind): boolean {
  if (kind === "personal") return team.personalOwner != null;
  if (kind === "shared") return team.personalOwner == null && team.deskId !== null;
  return true;
}

function matchesQuery(team: TeamIdentity, needle: string): boolean {
  if (needle === "") return true;
  const owner = team.personalOwner;
  return [team.deskName, owner?.displayName, owner?.email].some(
    (text) => text != null && fold(text).includes(needle),
  );
}

/** Name, nulls (deleted Teams) last, then id: the tiebreak every sort ends on. */
function byNameThenId(a: TeamIdentity, b: TeamIdentity): number {
  if (a.deskName !== b.deskName) {
    if (a.deskName === null) return 1;
    if (b.deskName === null) return -1;
    const named = a.deskName.localeCompare(b.deskName);
    if (named !== 0) return named;
  }
  return (a.deskId ?? "").localeCompare(b.deskId ?? "");
}

function sortTeams(teams: YagniCodeTeamUsageTotal[], sort: TeamSort): YagniCodeTeamUsageTotal[] {
  // "billed" is the report's own order: spend descending, ties already
  // broken by name then id on the server.
  if (sort === "billed") return [...teams];
  if (sort === "saved") {
    return [...teams].sort(
      (a, b) =>
        b.frontier.matched.savingsMillicents - a.frontier.matched.savingsMillicents ||
        byNameThenId(a, b),
    );
  }
  return [...teams].sort(byNameThenId);
}

export function shapeTeamRows(
  report: YagniCodeTeamAgentUsageReport,
  opts: TeamRowsOptions,
): TeamRowsView {
  const limit = opts.limit ?? TEAM_ROW_LIMIT;
  const named = sortTeams(
    report.teams.filter((team) => team.deskId !== null),
    opts.sort,
  );
  const noTeam = report.teams.find((team) => team.deskId === null) ?? null;
  const idleAll = report.idleTeams ?? [];
  const needle = fold(opts.query);
  const filtering = needle !== "" || opts.kind !== "all";
  const matches = (team: TeamIdentity) => matchesKind(team, opts.kind) && matchesQuery(team, needle);

  const idle = idleAll.filter(matches);
  const namedCount = named.length;
  const personalCount = [...named, ...idleAll].filter((team) => team.personalOwner != null).length;

  if (filtering) {
    const rows = named.filter(matches);
    const hidden = [...named.filter((team) => !matches(team)), ...(noTeam ? [noTeam] : [])];
    return {
      rows,
      noTeam: null,
      hidden,
      hiddenNamedCount: hidden.length - (noTeam ? 1 : 0),
      hiddenReason: hidden.length > 0 ? "filter" : null,
      idle,
      namedCount,
      personalCount,
      matchedCount: rows.length + idle.length,
    };
  }

  const rows = opts.showAll ? named : named.slice(0, limit);
  const hidden = opts.showAll ? [] : named.slice(limit);
  return {
    rows,
    noTeam,
    hidden,
    hiddenNamedCount: hidden.length,
    hiddenReason: hidden.length > 0 ? "cap" : null,
    idle,
    namedCount,
    personalCount,
    matchedCount: namedCount + idle.length,
  };
}

/**
 * The rollup row's name: "15 more Teams" under the cap, where No Team keeps
 * its own row; under a filter, the named Teams not shown, and No Team named
 * apart when it is folded in, so the count never calls No Team a Team.
 */
export function rollupLabel(view: TeamRowsView): string {
  const named = view.hiddenNamedCount;
  const teams = named === 1 ? "1 Team" : `${named} Teams`;
  if (view.hiddenReason === "cap") return named === 1 ? "1 more Team" : `${named} more Teams`;
  const withNoTeam = view.hidden.length > named;
  if (!withNoTeam) return `${teams} not shown`;
  return named === 0 ? "No Team not shown" : `${teams} and No Team not shown`;
}

/**
 * What the hidden Teams sum to, for the rollup row. Billed per Agent, the
 * billed total and runs are exact, because each run resolves to one Team.
 * Savings are never summed: each Team's Claude comparison is floored on its
 * own basis, so they do not add up to anything honest.
 */
export function sumHidden(
  cells: YagniCodeTeamAgentUsageCell[],
  hidden: YagniCodeTeamUsageTotal[],
): { byAgent: Map<string, number>; sellMillicents: number; runs: number } {
  const ids = new Set(hidden.map((team) => team.deskId));
  const byAgent = new Map<string, number>();
  for (const cell of cells) {
    if (!ids.has(cell.deskId)) continue;
    byAgent.set(cell.agentId, (byAgent.get(cell.agentId) ?? 0) + cell.sellMillicents);
  }
  let sellMillicents = 0;
  let runs = 0;
  for (const team of hidden) {
    sellMillicents += team.sellMillicents;
    runs += team.runs;
  }
  return { byAgent, sellMillicents, runs };
}
